/**
 * useTouchEdit — open an editor from a touch screen: double-tap OR long-press.
 *
 *   const touch = useTouchEdit(startEdit);
 *   <button {...touch} onDoubleClick={startEdit}>…</button>
 *
 * Why not just onDoubleClick: on phones a double-click is unreliable — iOS
 * Safari often never fires it, and a quick double-tap zooms the page instead.
 * Legacy handles the same two gestures by hand with the same timings
 * (InstancesListTable.vue handleTouchStart / handleTouchEnd / handleTap):
 *   - long-press: finger held 500 ms;
 *   - double-tap: two taps within 300 ms.
 *
 * A touch that moves (a scroll) cancels the long-press, so scrolling the table
 * never opens an editor. After a long-press opened the editor, the browser's
 * own long-press menu ("copy", "select") is suppressed.
 *
 * Mouse input is untouched: these are touch events only.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

export const LONG_PRESS_MS = 500;
export const DOUBLE_TAP_MS = 300;
/** How far a finger may drift (px) and still count as a press, not a scroll. */
const MOVE_TOLERANCE = 10;

export function useTouchEdit(onEdit) {
  const timer = useRef(null);
  const start = useRef(null);
  const lastTap = useRef(0);
  const longPressed = useRef(false);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Never leave a timer running after the cell is gone.
  useEffect(() => clear, [clear]);

  return useMemo(
    () => ({
      onTouchStart: (e) => {
        const t = e.touches[0];
        start.current = t ? { x: t.clientX, y: t.clientY } : null;
        longPressed.current = false;
        clear();
        timer.current = setTimeout(() => {
          timer.current = null;
          longPressed.current = true;
          onEdit();
        }, LONG_PRESS_MS);
      },
      onTouchMove: (e) => {
        const t = e.touches[0];
        if (!t || !start.current) return;
        const moved =
          Math.abs(t.clientX - start.current.x) > MOVE_TOLERANCE ||
          Math.abs(t.clientY - start.current.y) > MOVE_TOLERANCE;
        if (moved) clear(); // a scroll, not a press
      },
      onTouchEnd: () => {
        const wasHolding = timer.current !== null;
        clear();
        // The long-press already opened the editor; this lift is part of it.
        if (longPressed.current || !wasHolding) return;

        const now = Date.now();
        if (now - lastTap.current < DOUBLE_TAP_MS) {
          lastTap.current = 0;
          onEdit();
        } else {
          lastTap.current = now;
        }
      },
      onTouchCancel: clear,
      onContextMenu: (e) => {
        // The long-press opened the editor — not the browser's own menu.
        if (longPressed.current) e.preventDefault();
      },
    }),
    [clear, onEdit],
  );
}
